import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Home, 
  Truck, 
  Package,
  X,
  Building2,
  Database,
  FileText,
  MessageCircle,
  Users
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const SidebarContainer = styled.aside<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.white};
  border-right: 1px solid ${({ theme }) => theme.colors.gray200};
  z-index: 1000;
  transform: ${({ $isOpen }) => $isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform ${({ theme }) => theme.transitions.normal};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    transform: translateX(0);
    position: static;
    height: auto;
  }
`;

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  
  h2 {
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    margin: 0;
  }
`;

const CloseButton = styled.button`
  display: block;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.gray100};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const Navigation = styled.nav`
  padding: ${({ theme }) => theme.spacing.lg} 0;
`;

const NavSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h3`
  padding: 0 ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const NavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const NavItem = styled.li`
  margin: 0;
`;

const NavLinkStyled = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  border-left: 3px solid transparent;

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  &.active {
    background: ${({ theme }) => theme.colors.primary}10;
    color: ${({ theme }) => theme.colors.primary};
    border-left-color: ${({ theme }) => theme.colors.primary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transition: all ${({ theme }) => theme.transitions.fast};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { proveedor } = useAuth();

  // Verificar si el usuario es ADMINISTRADOR
  const isAdmin = proveedor?.username === 'ADMINISTRADOR';

  // Elementos de navegación del menú lateral
  const navigationItems: Array<{
    section: string;
    items: Array<{ to: string; icon: any; label: string }>;
  }> = [];

  // Sección para PROPONENTES (usuarios externos)
  if (!isAdmin) {
    navigationItems.push({
      section: 'Principal',
      items: [
        { to: '/rutas', icon: Package, label: 'Rutas Producto Terminado' },
        { to: '/rutas-granel', icon: Truck, label: 'Rutas Granel' },
        { to: '/preguntas', icon: MessageCircle, label: 'Preguntas' },
      ]
    });
  }

  // Sección de Administración solo para el ADMINISTRADOR
  if (isAdmin) {
    navigationItems.push({
      section: 'Administración',
      items: [
        { to: '/dashboard', icon: Home, label: 'Dashboard' },
        { to: '/licitaciones', icon: FileText, label: 'Licitaciones' },
        { to: '/proveedores', icon: Users, label: 'Proveedores' },
        { to: '/preguntas', icon: MessageCircle, label: 'Preguntas' },
        { to: '/base', icon: Database, label: 'Base de Datos' },
      ]
    });
  }

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={onClose} />
      <SidebarContainer $isOpen={isOpen}>
        <SidebarHeader>
          <Logo>
            <Building2 size={24} color="#2563eb" />
            <h2>LogiBid</h2>
          </Logo>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </SidebarHeader>

        <Navigation>
          {navigationItems.map((section, sectionIndex) => (
            <NavSection key={sectionIndex}>
              <SectionTitle>{section.section}</SectionTitle>
              <NavList>
                {section.items.map((item, itemIndex) => {
                  const Icon = item.icon;
                  return (
                    <NavItem key={itemIndex}>
                      <NavLinkStyled
                        to={item.to}
                        onClick={onClose}
                        className={location.pathname === item.to ? 'active' : ''}
                      >
                        <Icon />
                        {item.label}
                      </NavLinkStyled>
                    </NavItem>
                  );
                })}
              </NavList>
            </NavSection>
          ))}
        </Navigation>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
